import { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  VStack,
  HStack,
  Text,
  Box,
  Progress,
  Button,
  Icon,
} from '@chakra-ui/react';
import { Sparkles } from 'lucide-react';

interface RockEvaluationLoadingModalProps {
  isOpen: boolean;
  onCancel: () => void;
}

const loadingMessages = [
  'Analyzing SMART criteria...',
  'Checking clarity and scope...',
  'Evaluating impact and actionability...',
  'Generating recommendations...',
  'Almost done...',
];

/**
 * Rock Evaluation Loading Modal Component
 * Shows loading state while AI evaluation is in progress
 * Translated from Tailwind to Chakra UI
 */
export function RockEvaluationLoadingModal({
  isOpen,
  onCancel,
}: RockEvaluationLoadingModalProps) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showCancel, setShowCancel] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setMessageIndex(0);
      setProgress(0);
      setElapsedTime(0);
      setShowCancel(true);

      const messageInterval = setInterval(() => {
        setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 1500);

      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 10;
        });
      }, 300);

      const timeInterval = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);

      const cancelTimeout = setTimeout(() => {
        setShowCancel(false);
      }, 10000);

      return () => {
        clearInterval(messageInterval);
        clearInterval(progressInterval);
        clearInterval(timeInterval);
        clearTimeout(cancelTimeout);
      };
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={() => {}} isCentered size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalBody py={8}>
          <VStack spacing={6} align="center" justify="center">
            <Box position="relative">
              <Icon
                as={Sparkles}
                w={16}
                h={16}
                color="purple.600"
              />
              <Box
                position="absolute"
                top="-4px"
                right="-4px"
                w={4}
                h={4}
                bg="purple.600"
                borderRadius="full"
              />
            </Box>

            <VStack spacing={2} textAlign="center">
              <Text fontSize="2xl" fontWeight="bold" color="gray.900">
                AI Analysis
              </Text>
              <Text color="gray.600">
                {loadingMessages[messageIndex]}
              </Text>
            </VStack>

            <Box w="full" mb={2}>
              <Progress
                value={Math.min(progress, 90)}
                colorScheme="purple"
                size="sm"
                borderRadius="full"
                mb={2}
                bg="gray.200"
              />
              <HStack justify="space-between" fontSize="sm" color="gray.500">
                <Text>{Math.round(Math.min(progress, 90))}%</Text>
                <Text>{elapsedTime}s elapsed</Text>
              </HStack>
            </Box>

            <Text fontSize="sm" color="gray.500">
              ⏱ This usually takes 3-5 seconds
            </Text>

            {showCancel && (
              <Button variant="ghost" size="sm" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

